import type {
  EmailOtpType,
  Session,
  SupabaseClient,
} from "@supabase/supabase-js";
import {
  type AuthCallbackParams,
  parseAuthParamsFromUrl,
} from "./auth-callback";
import {
  resolveWebAuthReturnPath,
} from "./web-auth-routing";

const EMAIL_OTP_TYPES = new Set<EmailOtpType>([
  "signup",
  "invite",
  "magiclink",
  "recovery",
  "email_change",
  "email",
]);

export interface WebAuthRestoreResult {
  hasCallbackPayload: boolean;
  returnPath: string;
  session: Session | null;
}

export async function restoreSessionFromAuthParams(
  client: SupabaseClient,
  params: AuthCallbackParams,
): Promise<Session | null> {
  if (params.access_token && params.refresh_token) {
    const { data, error } = await client.auth.setSession({
      access_token: params.access_token,
      refresh_token: params.refresh_token,
    });
    if (error) throw error;
    return data.session;
  }

  if (params.code) {
    const { data, error } = await client.auth.exchangeCodeForSession(
      params.code,
    );
    if (error) throw error;
    return data.session;
  }

  if (params.token_hash && isEmailOtpType(params.type)) {
    const { data, error } = await client.auth.verifyOtp({
      token_hash: params.token_hash,
      type: params.type,
    });
    if (error) throw error;
    return data.session;
  }

  return null;
}

export async function restoreWebAuthSession(
  client: SupabaseClient,
  rawUrl = window.location.href,
): Promise<WebAuthRestoreResult> {
  const params = parseAuthParamsFromUrl(rawUrl);
  const hasCallbackPayload = hasAuthCallbackPayload(params);
  const session = hasCallbackPayload
    ? await restoreSessionFromAuthParams(client, params)
    : null;

  return {
    hasCallbackPayload,
    returnPath: resolveWebAuthReturnPath(params.return_to),
    session,
  };
}

export function clearWebAuthCallbackHistory(returnPath: string): void {
  if (typeof window === "undefined") return;
  window.history.replaceState({}, "", returnPath);
}

function hasAuthCallbackPayload(params: AuthCallbackParams): boolean {
  return Boolean(
    (params.access_token && params.refresh_token) ||
    params.code ||
    (params.token_hash && params.type),
  );
}

function isEmailOtpType(value?: string): value is EmailOtpType {
  return Boolean(value && EMAIL_OTP_TYPES.has(value as EmailOtpType));
}
